/**
 * FUNNEL · SCATTER · HEATMAP · TREEMAP · CONTRATO ÚNICO DE CHART
 *
 * Estes quatro tipos NÃO têm configuração própria. Eles usam o mesmo contrato
 * de `column`, `line` ou `pie`: `title`, `legend`, `tooltip`, `dataLabels`,
 * `xAxis.categories` e `series[].data`. O `type` apenas escolhe a representação.
 *
 * Para experimentar, copie qualquer bloco abaixo e troque somente `type`:
 *
 *   funnel   -> pontos da primeira série viram estágios, na ordem recebida
 *   pyramid  -> os mesmos estágios em triângulo; o primeiro item fica na base
 *   scatter  -> pontos { x, y } sobre o mesmo plano numérico do bubble
 *   heatmap  -> cada série é uma linha; cada posição de ponto é uma coluna;
 *               o valor define a intensidade da cor
 *   treemap  -> uma série: cada ponto é um bloco; várias séries: cada série
 *               é um grupo com seus blocos
 *
 * Os blocos são repetidos de propósito, como no resto do tutorial.
 */

/**
 * FUNNEL
 *
 * Os valores são desenhados na ordem em que aparecem em `data`. O nome de cada
 * estágio vem de `name` (ou de `xAxis.categories` quando o ponto não tem nome).
 * Clique na legenda para ocultar um estágio.
 *
 * Com legenda, o estágio mostra só o valor. Com `legend: false`, o nome de
 * cada estágio sai para fora, ligado por uma linha guia.
 */
export const funnelChart = {
  element: 'chart',
  type: 'funnel',
  title: 'Funnel — etapas de atendimento',
  subtitle: 'Valores decrescentes, ordem preservada',
  legend: 'right',

  tooltip: {
    title: '{name}',
    formatted: '{value} atendimentos'
  },

  dataLabels: {
    format: '{value}'
  },

  series: [
    {
      name: 'Atendimentos',
      data: [
        { name: 'Visitas', value: 1200 },
        { name: 'Contatos', value: 640 },
        { name: 'Propostas', value: 310 },
        { name: 'Contratos', value: 120 }
      ]
    }
  ],

  options: {
    width: 760,
    height: 456
  },

  style: {
    minWidth: '0'
  }
};

/**
 * PYRAMID
 *
 * Mesmo contrato do funnel: só o `type` muda. A altura de cada estágio é
 * proporcional ao valor e o primeiro item de `data` fica na base. Com
 * `legend: false`, os nomes saem para fora, ligados por uma linha guia.
 */
export const pyramidChart = {
  element: 'chart',
  type: 'pyramid',
  title: 'Pyramid — etapas de atendimento',
  subtitle: 'Altura proporcional ao valor',
  legend: false,

  tooltip: {
    title: '{name}',
    formatted: '{value} atendimentos'
  },

  dataLabels: {
    format: '{value}'
  },

  series: [
    {
      name: 'Atendimentos',
      data: [
        { name: 'Visitas', value: 1200 },
        { name: 'Contatos', value: 640 },
        { name: 'Propostas', value: 310 },
        { name: 'Contratos', value: 120 }
      ]
    }
  ],

  options: {
    width: 760,
    height: 456
  },

  style: {
    minWidth: '0'
  }
};

/**
 * SCATTER
 *
 * Cada ponto usa `x` e `y`. Sem `x`, a posição do ponto na série é usada.
 * Eixos, grid e títulos são os mesmos de qualquer gráfico cartesiano.
 * Rótulos só aparecem com `dataLabels.enabled: true`.
 */
export const scatterChart = {
  element: 'chart',
  type: 'scatter',
  title: 'Scatter — horas de estudo x nota',
  subtitle: 'Duas turmas comparadas',
  legend: 'bottom',

  tooltip: {
    title: '{name}',
    formatted: '{value}'
  },

  dataLabels: {
    enabled: false
  },

  xAxis: {
    title: 'Horas de estudo',
    labels: { format: '{value}' },
    grid: 'soft'
  },

  yAxis: {
    title: 'Nota',
    labels: { format: '{value}' },
    grid: 'full'
  },

  series: [
    {
      name: 'Turma A',
      data: [
        { x: 1, y: 4.5 },
        { x: 2, y: 5.8 },
        { x: 3, y: 6.1 },
        { x: 4, y: 7.4 },
        { x: 5, y: 8.2 },
        { x: 6, y: 8.9 }
      ]
    },
    {
      name: 'Turma B',
      options: {
        color: '#FF6000'
      },
      data: [
        { x: 1.5, y: 3.8 },
        { x: 2.5, y: 4.9 },
        { x: 3.5, y: 6.6 },
        { x: 4.5, y: 6.9 },
        { x: 5.5, y: 7.7 }
      ]
    }
  ],

  options: {
    width: 760,
    height: 456
  },

  style: {
    minWidth: '0'
  }
};

/**
 * HEATMAP
 *
 * Cada série é uma LINHA (`name` da série). Cada posição de `data` é uma
 * COLUNA, nomeada por `name` do ponto ou por `xAxis.categories`.
 * O valor define a intensidade da cor. A escala mínimo -> máximo aparece
 * abaixo da grade; a legenda de séries fica desligada por padrão.
 */
export const heatmapChart = {
  element: 'chart',
  type: 'heatmap',
  title: 'Heatmap — chamados por dia e turno',
  subtitle: 'Quanto mais intensa a cor, maior o volume',

  tooltip: {
    title: '{name}',
    formatted: '{value} chamados'
  },

  dataLabels: {
    format: '{value}'
  },

  series: [
    {
      name: 'Segunda',
      data: [
        { name: 'Manhã', value: 12 },
        { name: 'Tarde', value: 30 },
        { name: 'Noite', value: 8 }
      ]
    },
    {
      name: 'Terça',
      data: [
        { name: 'Manhã', value: 18 },
        { name: 'Tarde', value: 42 },
        { name: 'Noite', value: 11 }
      ]
    },
    {
      name: 'Quarta',
      data: [
        { name: 'Manhã', value: 25 },
        { name: 'Tarde', value: 55 },
        { name: 'Noite', value: 14 }
      ]
    },
    {
      name: 'Quinta',
      data: [
        { name: 'Manhã', value: 9 },
        { name: 'Tarde', value: 21 },
        { name: 'Noite', value: 6 }
      ]
    }
  ],

  options: {
    width: 760,
    height: 456
  },

  style: {
    minWidth: '0'
  }
};

/**
 * TREEMAP
 *
 * A área de cada bloco é proporcional ao valor. Com UMA série, cada ponto é
 * um bloco e a legenda lista os pontos. Com VÁRIAS séries, cada série é um
 * grupo (uma cor) e seus pontos são os blocos dentro do grupo.
 * Este exemplo usa várias séries.
 */
export const treemapChart = {
  element: 'chart',
  type: 'treemap',
  title: 'Treemap — custos por área',
  subtitle: 'Cada grupo reúne seus itens de custo',
  legend: 'bottom',

  tooltip: {
    title: '{name}',
    formatted: 'R$ {value} mil'
  },

  dataLabels: {
    format: '{value}'
  },

  series: [
    {
      name: 'Tecnologia',
      data: [
        { name: 'Nuvem', value: 120 },
        { name: 'Licenças', value: 80 },
        { name: 'Suporte', value: 40 }
      ]
    },
    {
      name: 'Pessoas',
      data: [
        { name: 'Salários', value: 300 },
        { name: 'Treinamento', value: 45 }
      ]
    },
    {
      name: 'Operação',
      data: [
        { name: 'Aluguel', value: 90 },
        { name: 'Energia', value: 35 },
        { name: 'Logística', value: 60 }
      ]
    }
  ],

  options: {
    width: 760,
    height: 456
  },

  style: {
    minWidth: '0'
  }
};

export const chartCompositionCharts = [
  funnelChart,
  pyramidChart,
  scatterChart,
  heatmapChart,
  treemapChart
];
